import { useContext, useEffect, useState } from 'react'
import PhoneInput, { CountryData } from 'react-phone-input-2'
import { loadReCaptcha, ReCaptcha } from 'react-recaptcha-v3'
import Select from 'react-select'
import useGymOptions from '../../../hooks/useGymOptions'
import { RECAPTCHA_KEY } from '../../../services/api/constants'
import { getSiteCode } from '../../../services/api/helpers'
import { FormsProps, SelectOption } from '../../../services/api/types'
import { SiteContext } from '../../../services/context/SiteContext'
import { deboundRequest, getChoices } from '../../../services/utils'
import * as G from '../../../styles/global.styles'
import { Container, Section } from '../../../styles/grid.styles'
import { colors } from '../../../styles/theme'
import {
  BlockTitle,
  CheckBox,
  DatePickerStyled,
  DropdownStyled,
  FileInput,
  IconEnums,
  Radio,
  SVGIcon,
  TextAreaInput,
  TextInput
} from '../../base'
import PrivacyField from '../PrivacyField'
import * as S from './Form.styled'

const selectedStyles = () => ({
  '> .address': {
    display: 'none'
  }
})

const selectGymStyles = {
  control: () => ({
    cursor: 'pointer',
    height: '48px',
    display: 'flex',
    borderRadius: 1,
    paddingRight: 8
  }),
  placeholder: () => ({
    color: `${colors.black}`
  }),
  option: () => ({
    borderBottom: `1px solid ${colors.grey__light}`,
    cursor: 'pointer',
    padding: 12,
    '&:hover': {
      backgroundColor: `${colors.grey__light}`
    }
  }),
  container: () => ({
    width: '100%',
    fontSize: 15,
    backgroundColor: `${colors.white__deep}`
  }),
  singleValue: () => ({ ...selectedStyles })
}

export interface StandardFormProps extends FormsProps {
  formik: any
  onSubmit?: Function
  onClose?: Function
  style?: React.CSSProperties
}

export const StandardForm = (props: StandardFormProps) => {
  const {
    fields,
    title,
    formik,
    preamble,
    submitText,
    formAction,
    slug,
    onClose,
    style,
    buttonColor,
    layout
  } = props
  const {
    gymOptions,
    isLoadingGyms,
    setChoosenState,
    setChoosenPostCode
  } = useGymOptions('', '', fields, slug)

  const { siteInfor } = useContext(SiteContext)
  const siteCode = getSiteCode(siteInfor?.root_url)

  const dropdownComponents = {
    IndicatorSeparator: () => null,
    DropdownIndicator: () => (
      <span style={{ position: 'relative', right: 6 }}>
        <SVGIcon name={IconEnums['icon-angle-down']} />
      </span>
    )
  }

  const hasRecaptcha =
    fields &&
    fields.length > 0 &&
    fields?.find(it => it.field_block === 'recaptcha')
      ? true
      : false

  const [recaptchaDisableSubmit, setRecaptchaDisableSubmit] = useState(
    hasRecaptcha
  )

  useEffect(() => {
    if (!window.RECAPTCHA_INITIALIZED && hasRecaptcha) {
      loadReCaptcha(RECAPTCHA_KEY)
      window.RECAPTCHA_INITIALIZED = true
    }
  }, [hasRecaptcha])

  useEffect(() => {
    if (formik.errors && formik.errors['recaptcha']) {
      updateRecaptcha()
    }
  }, [formik])

  const updateRecaptcha = async () => {
    const localRecaptcha = await localStorage.getItem('recaptcha')

    if (
      !formik.values['recaptcha'] ||
      formik.values['recaptcha'] !== localRecaptcha
    ) {
      await formik.setFieldValue('recaptcha', localRecaptcha, false)
      setRecaptchaDisableSubmit(false)
    }
  }

  const formatGymOption = ({ label, address, status }: any, { context }: any) =>
    context === 'value' ? (
      <div>{label}</div>
    ) : (
      <div style={{ padding: `0px 0px` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h5
            className='location'
            style={{
              fontSize: 16,
              lineHeight: `23px`,
              marginTop: 0,
              marginBottom: 0
            }}
          >
            {label}
          </h5>
          <div className='status' style={{ color: '#EE6C16' }}>
            {status}
          </div>
        </div>
        {address && (
          <p
            className='address'
            style={{
              marginBottom: 0,
              textAlign: 'left',
              color: 'grey'
            }}
          >
            {address}
          </p>
        )}
      </div>
    )

  return (
    <Section id={slug} style={style}>
      <Container>
        {onClose && (
          <S.CloseWrapper onClick={() => onClose()}>
            <SVGIcon name={IconEnums.close} />
          </S.CloseWrapper>
        )}
        {title && <BlockTitle isYouFitness>{title}</BlockTitle>}
        {preamble && <S.PreFormText>{preamble}</S.PreFormText>}
        <S.Form
          action={formAction}
          onSubmit={formik.handleSubmit}
          layout={layout}
        >
          <S.FormFields>
            {fields?.map((field, idx) => {
              let inputComp
              const isInvalid =
                formik &&
                formik.errors[field.name] &&
                formik.touched[field.name]
              switch (field.type) {
                case 'text_area': {
                  switch (field.field_block) {
                    case 'label':
                      return (
                        <S.FormGroup col={1} key={idx}>
                          <S.FormLabel error={isInvalid}>
                            {field.label}
                          </S.FormLabel>
                        </S.FormGroup>
                      )
                    case 'heading':
                      return <BlockTitle isYouFitness>{field.label}</BlockTitle>
                    case 'multiline':
                      inputComp = (
                        <TextAreaInput
                          rows={4}
                          cols={28}
                          name={field.name}
                          borderColor={isInvalid ? 'red' : undefined}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      )
                      break
                  }
                  break
                }
                case 'select': {
                  const selectOptions = getChoices(field.choices)
                  if (field.field_block == 'gym') {
                    inputComp = (
                      <Select
                        isLoading={isLoadingGyms}
                        options={gymOptions}
                        name={field.name}
                        styles={selectGymStyles}
                        isMulti={false}
                        formatOptionLabel={formatGymOption}
                        classNamePrefix={
                          isInvalid ? 'error you-fitness' : 'you-fitness'
                        }
                        onBlur={formik.handleBlur}
                        defaultValue={
                          (formik?.values?.[field.name] &&
                            gymOptions?.find(
                              (option: any) =>
                                option.value === formik.values[field.name]
                            )) ||
                          null
                        }
                        components={dropdownComponents}
                        onChange={(option: any) => {
                          formik.setFieldValue(field.name, option?.value || '')
                          formik.setFieldValue('selectedGym', option)
                        }}
                        placeholder={field.help_text}
                      />
                    )
                  } else {
                    inputComp = (
                      <DropdownStyled
                        classNamePrefix={
                          isInvalid ? 'error you-fitness' : 'you-fitness'
                        }
                        placeholder={field.help_text}
                        name={field.name}
                        options={selectOptions}
                        components={dropdownComponents}
                        onChange={(option: SelectOption) => {
                          if (field.name === 'state') {
                            setChoosenState(option.value)
                          }
                          formik.setFieldValue(field.name, option.value)
                        }}
                        onBlur={formik.handleBlur}
                      />
                    )
                  }
                  break
                }
                case 'checkbox': {
                  const selectOptions = getChoices(field.choices)

                  if (field.field_block === 'multi_select') {
                    inputComp = (
                      <DropdownStyled
                        classNamePrefix={
                          isInvalid ? 'error you-fitness' : 'you-fitness'
                        }
                        placeholder={field.help_text}
                        name={field.name}
                        isMulti
                        options={selectOptions}
                        components={dropdownComponents}
                        onChange={(options: SelectOption[]) => {
                          formik.setFieldValue(
                            field.name,
                            options?.map(it => it.value) ?? ''
                          )
                        }}
                        onBlur={formik.handleBlur}
                        maxMenuHeight={160}
                      />
                    )
                  } else {
                    inputComp = (
                      <>
                        {selectOptions.map(op => {
                          return (
                            <CheckBox
                              name={field.name}
                              type={field.type}
                              value={op.value}
                              label={op.label}
                              color='black'
                              borderColor={isInvalid ? 'red' : 'white'}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                          )
                        })}
                      </>
                    )
                  }
                  break
                }
                case 'radio': {
                  const selectOptions = getChoices(field.choices)
                  inputComp = (
                    <>
                      {selectOptions.map(op => {
                        return (
                          <Radio
                            name={field.name}
                            type={field.type}
                            value={op.value}
                            label={op.label}
                            color='black'
                            borderColor={isInvalid ? 'red' : 'white'}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        )
                      })}
                    </>
                  )
                  break
                }
                case 'file': {
                  inputComp = (
                    <FileInput
                      name={field.name}
                      type={field.type}
                      text={field.label}
                      className={`you-fitness-fileinput ${
                        isInvalid ? 'you-fitness-error' : ''
                      }`}
                      color='black'
                      borderColor={isInvalid ? 'red' : 'white'}
                      onChange={(event: any) =>
                        formik?.setFieldValue(
                          field.name,
                          event.currentTarget.files[0]
                        )
                      }
                      onBlur={formik.handleBlur}
                    />
                  )
                  break
                }
                case 'text': {
                  if (field.field_block == 'date') {
                    inputComp = (
                      <DatePickerStyled
                        name={field.name}
                        setFieldValue={formik?.setFieldValue}
                        showYearDropdown
                      />
                    )
                  } else if (field.field_block == 'datetime') {
                    inputComp = (
                      <DatePickerStyled
                        timePicker={true}
                        name={field.name}
                        setFieldValue={formik?.setFieldValue}
                        showYearDropdown
                      />
                    )
                  } else if (field.field_block == 'phone_number') {
                    inputComp = (
                      <PhoneInput
                        country={siteCode}
                        enableSearch
                        countryCodeEditable={false}
                        placeholder={field.help_text}
                        value={formik.values[field.name]}
                        onChange={(value: any, data: CountryData) => {
                          formik.setFieldValue(field.name, `+${value}`)
                          formik.setFieldValue(
                            'country-code',
                            `+${data.dialCode}`
                          )
                        }}
                        onBlur={formik.handleBlur}
                      />
                    )
                  } else {
                    inputComp = (
                      <TextInput
                        placeholder={field.help_text}
                        name={field.name}
                        type={field.type}
                        color='black'
                        borderColor={isInvalid ? 'red' : 'white'}
                        onChange={(e: any) => {
                          if (field.name === 'postcode') {
                            deboundRequest(setChoosenPostCode, e.target.value)
                          }
                          formik.setFieldValue(field.name, e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                      />
                    )
                  }
                  break
                }
                case 'input': {
                  if (field.field_block == 'recaptcha') {
                    inputComp = (
                      <ReCaptcha
                        sitekey={RECAPTCHA_KEY}
                        action={formAction || 'submit'}
                        verifyCallback={async (token: any) => {
                          if (formik.values['recaptcha'] !== token)
                            await formik.setFieldValue(
                              'recaptcha',
                              token,
                              false
                            )
                          localStorage.setItem('recaptcha', token)
                          setRecaptchaDisableSubmit(false)
                        }}
                      />
                    )
                  } else {
                    inputComp = (
                      <TextInput
                        placeholder={field.help_text}
                        name={field.name}
                        type={field.type}
                        color='black'
                        borderColor={isInvalid ? 'red' : 'white'}
                        onChange={(e: any) => {
                          if (field.name === 'postcode') {
                            deboundRequest(setChoosenPostCode, e.target.value)
                          }
                          formik.setFieldValue(field.name, e.target.value)
                        }}
                        onBlur={formik.handleBlur}
                      />
                    )
                  }
                  break
                }
                default:
                  inputComp = (
                    <TextInput
                      placeholder={field.help_text}
                      name={field.name}
                      type={field.type}
                      color='black'
                      borderColor={isInvalid ? 'red' : 'white'}
                      onChange={(e: any) => {
                        if (field.name === 'postcode') {
                          deboundRequest(setChoosenPostCode, e.target.value)
                        }
                        formik.setFieldValue(field.name, e.target.value)
                      }}
                      onBlur={formik.handleBlur}
                    />
                  )
                  break
              }
              return (
                <S.FormGroup
                  className={
                    field.field_block === 'privacypolicy'
                      ? 'form-group privacy-policy'
                      : 'form-group'
                  }
                  style={{
                    display:
                      field.field_block === 'recaptcha' ||
                      field.type === 'hidden'
                        ? 'none'
                        : undefined
                  }}
                >
                  {field.field_block === 'privacypolicy' ? (
                    <div>
                      <div
                        style={{ display: 'flex', justifyContent: 'center' }}
                      >
                        <PrivacyField
                          field={field}
                          formik={formik}
                          isYouFitnessTemplate
                        />
                      </div>
                      <div className='text-center'>
                        <S.ErrorMessage>
                          {formik?.touched[field.name] &&
                            formik?.errors[field.name]}
                        </S.ErrorMessage>
                      </div>
                    </div>
                  ) : (
                    <>
                      {(field.type !== 'checkbox' ||
                        (field.type === 'checkbox' &&
                          getChoices(field.choices).length > 1)) && (
                        <S.FormLabel
                          error={
                            formik.touched[field.name] &&
                            formik.errors[field.name]
                          }
                        >
                          {field.label}
                        </S.FormLabel>
                      )}
                      {inputComp}
                      <S.ErrorMessage>
                        {formik?.touched[field.name] &&
                          formik?.errors[field.name]}
                      </S.ErrorMessage>
                    </>
                  )}
                </S.FormGroup>
              )
            })}
          </S.FormFields>
          {formik.isSubmitting ? (
            <G.SpinnerWrap isYouFitness>
              <G.Spinner />
            </G.SpinnerWrap>
          ) : (
            <S.ButtonWrap>
              <G.BlueButton
                type='submit'
                color={buttonColor}
                disabled={formik.isSubmitting || recaptchaDisableSubmit}
              >
                {submitText || 'Submit'}
              </G.BlueButton>
            </S.ButtonWrap>
          )}
        </S.Form>
      </Container>
    </Section>
  )
}

export default StandardForm
